import { Directive, ElementRef, HostListener, Input, OnInit, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TableUtils } from 'src/app/utils/table-utils';

const THOUSAND_SEPARATOR = '.';
const DECIMAL_SEPARATOR = ',';
const NAVIGATION_KEYS = new Set(['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End']);
const CLIPBOARD_KEYS = new Set(['a', 'c', 'v', 'x']);

@Directive({
  selector: '[appCurrencyInput]',
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => CurrencyInputDirective),
    multi: true
  }],
  standalone: true
})
export class CurrencyInputDirective implements ControlValueAccessor, OnInit {
  @Input() decimalPlaces = 2;

  private readonly el: HTMLInputElement;
  private onChange: (value: number | null) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(elementRef: ElementRef<HTMLInputElement>) {
    this.el = elementRef.nativeElement;
  }

  ngOnInit(): void {
    this.el.setAttribute('inputmode', 'decimal');
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (this.isAllowedKey(event)) return;
    event.preventDefault();
  }

  @HostListener('input')
  onInput(): void {
    const rawValue = this.removeThousandSeparators(this.el.value);
    const truncatedValue = this.truncateDecimals(rawValue);
    const formatted = this.formatWithThousandSeparator(truncatedValue);

    if (this.el.value !== formatted) {
      const cursorPos = this.calculateCursorPosition(this.el.value, formatted, this.el.selectionStart ?? 0);
      this.el.value = formatted;
      this.el.setSelectionRange(cursorPos, cursorPos);
    }

    this.onChange(this.parseToNumber(formatted));
  }

  @HostListener('blur')
  onBlur(): void {
    this.onTouched();
    if (this.el.value) {
      const numericValue = this.parseToNumber(this.el.value);
      if (numericValue !== null) {
        this.el.value = this.formatNumber(numericValue);
      }
    }
  }

  @HostListener('focus')
  onFocus(): void {
    setTimeout(() => this.el.select(), 0);
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pastedText = event.clipboardData?.getData('text') ?? '';
    const cleaned = this.sanitizePastedValue(pastedText);
    const formatted = this.formatWithThousandSeparator(cleaned);
    
    this.el.value = formatted;
    this.onChange(this.parseToNumber(formatted));
  }

  writeValue(value: number | null): void {
    this.el.value = value == null ? '' : this.formatNumber(value);
  }

  registerOnChange(fn: (value: number | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.el.disabled = isDisabled;
  }

  private isAllowedKey(event: KeyboardEvent): boolean {
    if (NAVIGATION_KEYS.has(event.key)) return true;
    if (event.ctrlKey && CLIPBOARD_KEYS.has(event.key.toLowerCase())) return true;
    if (/^\d$/.test(event.key)) return true;
    if (event.key === DECIMAL_SEPARATOR && !this.el.value.includes(DECIMAL_SEPARATOR)) return true;
    if (event.key === '-' && this.el.selectionStart === 0 && !this.el.value.includes('-')) return true;
    return false;
  }

  private removeThousandSeparators(value: string): string {
    return value.split(THOUSAND_SEPARATOR).join('');
  }

  private truncateDecimals(value: string): string {
    if (!value.includes(DECIMAL_SEPARATOR)) return value;
    
    const [intPart, decPart] = value.split(DECIMAL_SEPARATOR);
    if (decPart && decPart.length > this.decimalPlaces) {
      return `${intPart}${DECIMAL_SEPARATOR}${decPart.substring(0, this.decimalPlaces)}`;
    }
    return value;
  }

  private formatNumber(value: number): string {
    return TableUtils.formatDecimal(value, this.decimalPlaces, { minDecimals: this.decimalPlaces });
  }

  private formatWithThousandSeparator(value: string): string {
    if (!value) return '';

    const isNegative = value.startsWith('-');
    const cleanValue = isNegative ? value.slice(1) : value;
    const [intPart, decPart] = cleanValue.split(DECIMAL_SEPARATOR);

    let formattedInt = intPart.replace(/^0+/, '') || '0';
    formattedInt = formattedInt.replace(/\B(?=(\d{3})+(?!\d))/g, THOUSAND_SEPARATOR);

    const result = decPart === undefined ? formattedInt : `${formattedInt}${DECIMAL_SEPARATOR}${decPart}`;
    return isNegative ? `-${result}` : result;
  }

  private parseToNumber(formattedValue: string): number | null {
    if (!formattedValue) return null;

    const normalized = formattedValue
      .split(THOUSAND_SEPARATOR).join('')
      .replace(DECIMAL_SEPARATOR, '.');
    const num = Number.parseFloat(normalized);

    return Number.isNaN(num) ? null : num;
  }

  private sanitizePastedValue(text: string): string {
    let cleaned = text.replace(/[^\d,.\-]/g, '');

    if (cleaned.includes(DECIMAL_SEPARATOR)) {
      cleaned = this.removeThousandSeparators(cleaned);
    } else {
      const dotCount = (cleaned.match(/\./g) ?? []).length;
      if (dotCount > 1) {
        cleaned = this.removeThousandSeparators(cleaned);
      } else if (dotCount === 1) {
        cleaned = cleaned.replace(THOUSAND_SEPARATOR, DECIMAL_SEPARATOR);
      }
    }

    return cleaned;
  }

  private calculateCursorPosition(oldValue: string, newValue: string, oldCursor: number): number {
    const countDots = (str: string, end: number) => (str.substring(0, end).match(/\./g) ?? []).length;
    const oldDots = countDots(oldValue, oldCursor);
    const newDots = countDots(newValue, oldCursor);
    const newCursor = oldCursor + (newDots - oldDots);

    return Math.max(0, Math.min(newCursor, newValue.length));
  }
}
